import {
  Button,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { kebabCase } from 'lodash-es'
import type { FormEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { match } from 'ts-pattern'
import { useMe } from 'src/services/helpers/user'
import type { PromptsModerationAction } from 'src/utils/contentSafety'
import { track } from 'src/utils/tracker'

type SensitiveTrackingParams = {
  prompts?: string
  taskId?: string
  modelId?: string
}

function SensitiveFeedbackDialogContent(props: {
  onClose: () => void
  moderationAction: PromptsModerationAction
  trackingParams: SensitiveTrackingParams
}) {
  const [explicit, setExplicit] = useState<
    'non-explicit' | 'non-underage' | 'neither'
  >()

  const { beingMember } = useMe()

  const { t } = useTranslation(['generation', 'action'])
  const feedbackTypeKey = kebabCase(props.moderationAction)

  const submit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      track(
        'nsfw_prompts_feedback',
        {
          ...props.trackingParams,
          isMember: beingMember,
          promptsModerationAction: props.moderationAction,
          ...match(explicit)
            .with('non-explicit', () => ({
              explicit: false,
              underage: true,
            }))
            .with('non-underage', () => ({
              explicit: true,
              underage: false,
            }))
            .with('neither', () => ({
              explicit: false,
              underage: false,
            }))
            .otherwise(() => ({})),
        },
        { destinations: ['getway'] },
      )
      props.onClose()
    },
    [beingMember, explicit, props.moderationAction, props.trackingParams],
  )

  return (
    <form onSubmit={submit}>
      <h1 className="text-2xl font-semibold">
        {t(`generation:submit.notice.prompts-filter.${feedbackTypeKey}.title`)}
      </h1>
      <p className="text-sm mt-2">
        {t(
          `generation:submit.notice.prompts-filter.${feedbackTypeKey}.description`,
        )}
      </p>
      <details className="pl-2 pt-4 text-sm">
        <summary>
          {t('generation:submit.notice.prompts-filter.options.summary')}
        </summary>
        <RadioGroup
          name="explicit-radio-group"
          value={explicit}
          onChange={e => setExplicit(e.target.value as typeof explicit)}
          className="gap-2 mt-1 pl-1"
        >
          <FormControlLabel
            disableTypography
            value={'non-explicit'}
            control={<Radio size="small" className="text-neutral-600" />}
            label={
              <span className="text-sm">
                {t(
                  'generation:submit.notice.prompts-filter.options.non-explicit',
                )}
              </span>
            }
          />
          <FormControlLabel
            disableTypography
            value={'non-underage'}
            control={<Radio size="small" className="text-neutral-600" />}
            label={
              <span className="text-sm">
                {t(
                  'generation:submit.notice.prompts-filter.options.non-underage',
                )}
              </span>
            }
          />
          <FormControlLabel
            disableTypography
            value={'neither'}
            control={<Radio size="small" className="text-neutral-600" />}
            label={
              <span className="text-sm">
                {t('generation:submit.notice.prompts-filter.options.neither')}
              </span>
            }
          />
        </RadioGroup>
      </details>
      <DialogActions>
        <Button className="text-black" onClick={props.onClose}>
          {t('action:cancel')}
        </Button>
        <Button
          className="bg-black text-white disabled:bg-neutral-400"
          disabled={explicit == undefined}
          variant="contained"
          type="submit"
        >
          {t('action:confirm')}
        </Button>
      </DialogActions>
    </form>
  )
}

// export function openSensitiveFeedbackDialog(
//   moderationAction: PromptsModerationAction,
//   trackingParams: SensitiveTrackingParams,
// ) {
//   return new Promise<void>((resolve, reject) => {
//     const instanceRef = dialog({
//       children: (
//         <SensitiveFeedbackDialogContent
//           onClose={() => {
//             instanceRef.current?.close()
//           }}
//           moderationAction={moderationAction}
//           trackingParams={trackingParams}
//         />
//       ),
//       onClose: () => resolve(),
//       fullWidth: false,
//       responsive: false,
//     })
//   })
// }

export function openSensitiveFeedbackToast(
  moderationAction: PromptsModerationAction,
  trackingParams: SensitiveTrackingParams,
) {
  const toastId = toast(
    <SensitiveFeedbackDialogContent
      moderationAction={moderationAction}
      trackingParams={trackingParams}
      onClose={() => {
        toast.dismiss(toastId)
      }}
    />,
    {
      autoClose: false,
      closeOnClick: false,
      className: '!cursor-default',
    },
  )
  return
}
